import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { t } from '@/i18n'

export const useNotificationStore = defineStore('notifications', () => {
  const notifications = ref([])
  const maxNotifications = 5

  const addNotification = (notification) => {
    const id = Date.now() + Math.random()

    const newNotification = {
      id,
      type: notification.type || 'info',
      title: notification.title || '',
      message: notification.message || '',
      duration: notification.duration || 5000,
      icon: notification.icon || null,
      action: notification.action || null,
      persistent: notification.persistent || false,
      createdAt: Date.now()
    }

    notifications.value.unshift(newNotification)

    if (notifications.value.length > maxNotifications) {
      notifications.value = notifications.value.slice(0, maxNotifications)
    }

    if (!newNotification.persistent) {
      setTimeout(() => {
        removeNotification(id)
      }, newNotification.duration)
    }

    return id
  }

  const removeNotification = (id) => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  const clearAll = () => {
    notifications.value = []
  }

  const success = (message, title = t('notifications.success')) => {
    return addNotification({
      type: 'success',
      title,
      message,
      icon: '✓'
    })
  }

  const error = (message, title = t('notifications.error')) => {
    return addNotification({
      type: 'error',
      title,
      message,
      duration: 8000,
      icon: '✕'
    })
  }

  const warning = (message, title = t('notifications.warning')) => {
    return addNotification({
      type: 'warning',
      title,
      message,
      duration: 7000,
      icon: '⚠'
    })
  }

  const info = (message, title = t('notifications.info')) => {
    return addNotification({
      type: 'info',
      title,
      message,
      icon: 'ℹ'
    })
  }

  const win = (amount, gameName) => {
    return addNotification({
      type: 'success',
      title: `🎉 ${t('notifications.bigWinTitle')}`,
      message: t('notifications.bigWinMessage', { amount: `$${amount.toFixed(2)}`, game: gameName }),
      duration: 6000,
      icon: '💰',
      action: {
        label: t('notifications.playAgain'),
        callback: () => {
          window.location.reload()
        }
      }
    })
  }

  const depositSuccess = (amount) => {
    return addNotification({
      type: 'info',
      title: `💳 ${t('notifications.depositSentTitle')}`,
      message: t('notifications.depositSentMessage', { amount: `$${amount.toFixed(2)}` }),
      duration: 6000,
      icon: '⏳'
    })
  }

  const withdrawalSuccess = (amount) => {
    return addNotification({
      type: 'info',
      title: `💸 ${t('notifications.withdrawalSentTitle')}`,
      message: t('notifications.withdrawalSentMessage', { amount: `$${amount.toFixed(2)}` }),
      duration: 6000,
      icon: '⏳'
    })
  }

  const bonusClaimed = (bonusName, amount) => {
    return addNotification({
      type: 'success',
      title: `🎁 ${t('notifications.bonusClaimedTitle')}`,
      message: t('notifications.bonusClaimedMessage', { bonus: bonusName, amount: `$${amount.toFixed(2)}` }),
      duration: 6000,
      icon: '🎁'
    })
  }

  const favoriteAdded = (gameName) => {
    return addNotification({
      type: 'info',
      title: `❤️ ${t('notifications.favoriteAddedTitle')}`,
      message: t('notifications.favoriteAddedMessage', { game: gameName }),
      duration: 3000,
      icon: '❤️'
    })
  }

  const favoriteRemoved = (gameName) => {
    return addNotification({
      type: 'info',
      title: `💔 ${t('notifications.favoriteRemovedTitle')}`,
      message: t('notifications.favoriteRemovedMessage', { game: gameName }),
      duration: 3000,
      icon: '💔'
    })
  }

  const activeNotifications = computed(() => notifications.value)
  const notificationCount = computed(() => notifications.value.length)

  return {
    notifications,
    activeNotifications,
    notificationCount,
    addNotification,
    removeNotification,
    clearAll,
    success,
    error,
    warning,
    info,
    win,
    depositSuccess,
    withdrawalSuccess,
    bonusClaimed,
    favoriteAdded,
    favoriteRemoved
  }
})
